// fp-stats.js
// Franchise Player — small shared pieces for stats: team logos, matchup tags,
// Vegas lines and number formatting. Used by the lineup builder, player card
// and standings.

import React, { useState } from "react";
import { findTeam, teamLogoUrl } from "./nfl-teams";
import { POSITION_COLORS } from "./fp-theme";

// Team logo (same source as Underdog Edge). Falls back to the abbreviation.
export function TeamLogo({ team, size = 20 }) {
  const [failed, setFailed] = useState(false);
  const t = findTeam(team);
  if (!t || failed) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size,
        fontSize: Math.max(8, size * 0.36), fontWeight: 800, color: "rgba(255,255,255,0.7)",
        background: "rgba(255,255,255,0.08)", borderRadius: 4, flexShrink: 0,
      }}>{team}</span>
    );
  }
  return (
    <img src={teamLogoUrl(t.espnId)} alt={team} width={size} height={size} onError={() => setFailed(true)}
      style={{ objectFit: "contain", flexShrink: 0 }} />
  );
}

export function posLabel(pos) {
  return pos === "DEF" ? "D/ST" : pos;
}

export function PosBadge({ pos, small }) {
  return (
    <span style={{
      fontSize: small ? 9 : 10, fontWeight: 800, padding: small ? "1px 4px" : "2px 6px", borderRadius: 4, minWidth: small ? 24 : 30,
      textAlign: "center", color: POSITION_COLORS[pos] || "white", background: "rgba(255,255,255,0.06)",
      border: `1px solid ${(POSITION_COLORS[pos] || "#fff")}55`, display: "inline-block", flexShrink: 0,
    }}>
      {posLabel(pos)}
    </span>
  );
}

export function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Matchup from the defense-vs-position table: rank 1 = toughest (fewest points
// allowed to that position), rank 32 = easiest.
export function matchupInfo(dvp, opp, pos) {
  const row = dvp?.teams?.[opp]?.[pos];
  if (!row) return null;
  const n = dvp.teamCount || 32;
  const easy = row.rank > n * 0.75, tough = row.rank <= n * 0.25;
  return {
    rank: row.rank, avg: row.avg, of: n,
    tone: easy ? "easy" : tough ? "tough" : "neutral",
    text: `#${row.rank} vs ${posLabel(pos)}`,
    title: pos === "DEF"
      ? `D/STs average ${row.avg} pts against ${opp}'s offense: ${ordinal(row.rank)} of ${n} (1 = toughest, ${n} = easiest)`
      : `${opp} allows ${row.avg} pts/game to ${posLabel(pos)}s: ${ordinal(row.rank)} of ${n} (1 = toughest, ${n} = easiest)`,
  };
}

const TONES = {
  easy: { color: "#4ADE80", border: "rgba(74,222,128,0.4)", bg: "rgba(74,222,128,0.1)" },
  tough: { color: "#F87171", border: "rgba(248,113,113,0.4)", bg: "rgba(248,113,113,0.1)" },
  neutral: { color: "rgba(255,255,255,0.65)", border: "rgba(255,255,255,0.15)", bg: "rgba(255,255,255,0.05)" },
};

export function MatchupTag({ info }) {
  if (!info) return null;
  const t = TONES[info.tone];
  return (
    <span title={info.title} style={{
      fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, whiteSpace: "nowrap",
      color: t.color, border: `1px solid ${t.border}`, background: t.bg,
    }}>
      {info.text}
    </span>
  );
}

// "O/U 43.5 · ATL 23.5" for a team from the odds doc.
export function vegasText(odds, team) {
  const o = odds?.teams?.[team];
  if (!o || o.total == null) return "";
  const spread = o.spread == null ? "" : o.spread === 0 ? " PK" : ` ${o.spread > 0 ? "+" : ""}${o.spread}`;
  return `O/U ${o.total} · ${team}${spread} · implied ${o.implied ?? "?"}`;
}

export function fmtPts(x) {
  if (x == null || !Number.isFinite(x)) return "—";
  return (Math.round(x * 10) / 10).toFixed(1);
}

// Projected points per $1,000 of salary.
export function valueOf(proj, salary) {
  if (proj == null || !Number.isFinite(proj) || !salary) return null;
  return proj / (salary / 1000);
}

export function fmtOdds(american) {
  if (american == null || american === "") return "";
  const s = String(american);
  return /^[+-]/.test(s) || s === "even" ? s : `+${s}`;
}

// ── Live games ────────────────────────────────────────────────────────────────
// The scoreboard doc (fp_scoreboard/{season}_{week}) is written every time live
// scoring pulls a box score (every 5 minutes during games).

// Team -> its game on the scoreboard.
export function scoreboardByTeam(scoreboard) {
  const out = {};
  for (const [id, g] of Object.entries(scoreboard?.games || {})) {
    out[g.away] = { id, ...g };
    out[g.home] = { id, ...g };
  }
  return out;
}

// "2nd 8:24" / "Q2 8:24" / "Halftime" / "End of 3rd" / "Overtime 2:10" -> "Q2 8:24", "Half", "End Q3", "OT 2:10"
export function fmtClock(clock) {
  let c = String(clock || "").trim();
  if (!c) return "";
  c = c.replace(/\b([1-4])(st|nd|rd|th)( quarter)?\b/gi, "Q$1").replace(/\bquarter ([1-4])\b/gi, "Q$1");
  c = c.replace(/\bhalf ?time\b/gi, "Half").replace(/\bovertime\b/gi, "OT").replace(/\bend of\b/gi, "End");
  c = c.replace(/\s*-\s*/g, " ").replace(/\s+/g, " ");
  return c;
}

// Live game state for one team's game. kickoff in ms; sbGame from scoreboardByTeam.
// Returns { state: "upcoming" | "starting" | "live" | "final", ... }.
export function gameState(sbGame, kickoff, now = Date.now()) {
  if (sbGame && sbGame.status === "final") return { state: "final", g: sbGame };
  if (sbGame && sbGame.status === "live") return { state: "live", g: sbGame, clock: fmtClock(sbGame.clock) };
  if (kickoff && kickoff <= now) return { state: "starting" };
  return { state: "upcoming" };
}

const LIVE_RED = "#F87171";
export const LIVE_CSS = "@keyframes fpLivePulse{0%,100%{opacity:1}50%{opacity:.3}}";

// "● LIVE · SF 17 @ ATL 10 · Q2 8:24" with the player's team in bold.
// Falls back to the usual "SF @ ATL · Sun 10:00 AM" before kickoff.
export function GameLine({ team, opp, home, kickoff, sbGame, kickoffText, compact }) {
  const st = gameState(sbGame, kickoff);
  const b = (t) => (t === team ? <b style={{ color: "white" }}>{t}</b> : t);
  if (st.state === "upcoming") {
    return <>{team}{opp ? ` ${home ? "vs" : "@"} ${opp}` : ""}{kickoffText ? ` · ${kickoffText}` : ""}</>;
  }
  if (st.state === "starting") {
    return <>{team}{opp ? ` ${home ? "vs" : "@"} ${opp}` : ""} · <span style={{ color: LIVE_RED }}>Kicked off</span></>;
  }
  const g = st.g;
  const score = <>{b(g.away)} {g.awayPts ?? 0} @ {b(g.home)} {g.homePts ?? 0}</>;
  if (st.state === "final") {
    const mine = g.away === team ? g.awayPts - g.homePts : g.homePts - g.awayPts;
    return <><span style={{ fontWeight: 700 }}>Final{mine > 0 ? " W" : mine < 0 ? " L" : " T"}</span> · {score}</>;
  }
  return (
    <>
      <span style={{ color: LIVE_RED, fontWeight: 800, letterSpacing: 0.5 }}>
        <span style={{ animation: "fpLivePulse 1.6s ease-in-out infinite", display: "inline-block" }}>●</span>{compact ? "" : " LIVE"}
      </span>
      {" · "}{score}{st.clock ? ` · ${st.clock}` : ""}
    </>
  );
}

// "Updated 3 min ago"
export function agoText(ms, now = Date.now()) {
  if (!ms) return "";
  const m = Math.round((now - ms) / 60000);
  return m <= 0 ? "updated just now" : m === 1 ? "updated 1 min ago" : m < 60 ? `updated ${m} min ago` : `updated ${Math.round(m / 60)} hr ago`;
}

// Short live stat line for a player (or D/ST) from a stored box score.
export function statLine(pos, s) {
  if (!s) return "";
  const n = (x) => x || 0;
  const plural = (x, one, many) => `${x} ${x === 1 ? one : many}`;
  const parts = [];
  if (pos === "DEF") {
    if (n(s.sacks)) parts.push(plural(s.sacks, "sack", "sacks"));
    if (n(s.int)) parts.push(`${s.int} INT`);
    if (n(s.fumRec)) parts.push(`${s.fumRec} FR`);
    if (n(s.td)) parts.push(plural(s.td, "TD", "TDs"));
    if (n(s.safety)) parts.push(plural(s.safety, "safety", "safeties"));
    parts.push(`${n(s.ptsAllowed)} pts allowed`);
    return parts.join(" · ");
  }
  const rush = () => {
    if (n(s.rushAtt) || n(s.rushYd)) parts.push(`${n(s.rushAtt)} car · ${n(s.rushYd)} rush yds`);
    if (n(s.rushTD)) parts.push(`${s.rushTD} rush TD`);
  };
  const recv = () => {
    if (n(s.rec) || n(s.tgt)) parts.push(`${n(s.rec)} rec${n(s.tgt) ? ` (${s.tgt} tgt)` : ""} · ${n(s.recYd)} yds`);
    if (n(s.recTD)) parts.push(`${s.recTD} rec TD`);
  };
  if (pos === "QB") {
    parts.push(`${n(s.passCmp)}/${n(s.passAtt)} · ${n(s.passYd)} yds`);
    if (n(s.passTD)) parts.push(`${s.passTD} TD`);
    if (n(s.int)) parts.push(`${s.int} INT`);
    rush();
  } else if (pos === "RB") { rush(); recv(); }
  else { recv(); rush(); }
  if (n(s.retTD)) parts.push(`${s.retTD} return TD`);
  if (n(s.fumLost)) parts.push(`${s.fumLost} fum lost`);
  return parts.join(" · ") || "No stats yet";
}

// ── Projected finish ─────────────────────────────────────────────────────────
// Same logic as fpGameRemaining / fpProjFinish in functions/index.js: finished games
// count actual points, games in progress count points so far plus the projection for
// the share of the game left, unstarted games count the projection.
export function gameRemaining(sbGame, kickoff, now = Date.now()) {
  if (sbGame && sbGame.status === "final") return 0;
  if (!sbGame) {
    if (!kickoff || kickoff > now) return 1;
    return Math.max(0, 1 - (now - kickoff) / (190 * 60000));
  }
  const c = String(sbGame.clock || "").toLowerCase();
  if (/overtime|\bot\b/.test(c)) return 0;
  if (/half/.test(c)) return 0.5;
  const qm = c.match(/\b([1-4])(?:st|nd|rd|th)\b/) || c.match(/\bq([1-4])\b/) || c.match(/quarter ([1-4])/);
  if (qm) {
    const q = +qm[1];
    if (/end/.test(c)) return Math.max(0, (4 - q) / 4);
    const tm = c.match(/(\d{1,2}):(\d{2})/);
    const left = tm ? +tm[1] + +tm[2] / 60 : 7.5;
    return Math.min(1, Math.max(0, ((4 - q) * 15 + Math.min(15, left)) / 60));
  }
  if (!kickoff || kickoff > now) return 1;
  return Math.max(0, 1 - (now - kickoff) / (190 * 60000));
}

// players: [{ id, team, actual, proj, mult }] -> projected finish
export function projFinish(players, gameFor, kickoffOf, now = Date.now()) {
  let total = 0;
  for (const p of players) {
    const rem = gameRemaining(gameFor(p.team), kickoffOf(p.team), now);
    total += ((p.actual || 0) + (p.proj != null && Number.isFinite(p.proj) ? p.proj * rem : 0)) * (p.mult || 1);
  }
  return Math.round(total * 100) / 100;
}
